/**
 * Per-country configuration layer (WS1 — Peru localization).
 *
 * Single source of truth for country-dependent behaviour (currency, locale,
 * IGV/IVA rate, timezone, ID types, address levels, ...). The data lives in the
 * Firestore `params/global.countries` map, exposed to the app as
 * `parametrosDB.countries` via the Parameters context.
 *
 * Fail-closed contract: resolvers THROW when the requested country (or a
 * required value such as VAT) is missing, instead of silently falling back to a
 * Chilean default. A silent 19%-vs-18% IGV or CLP-vs-PEN substitution is a
 * financial bug, so it must surface loudly.
 *
 * Selectors accept an explicit `countries` map (preferred at call sites that
 * already hold `parametrosDB`) but also fall back to a module-level cache that
 * the Parameters context hydrates via `setCountriesConfig`, so WS3 consumers can
 * call them without threading the map everywhere.
 */

/** @typedef {Object} CountryConfig
 * @property {string} currency
 * @property {string} currencySymbol
 * @property {string} locale
 * @property {number} decimals
 * @property {number} vatRate
 * @property {string} vatLabel
 * @property {string} timezone
 * @property {number} utcOffset
 * @property {string} holidayCC
 * @property {string[]} idTypes
 * @property {string} primaryIdType
 * @property {string[]} addressLevels
 * @property {string} societyTypesKey
 * @property {string} phoneCC
 * @property {string} baseCurrency
 * @property {number} [defaultGastoOpe] default de gastos de operación en la moneda funcional
 * @property {Object<string, number>} [defaultGastoOpePorMoneda] idem por moneda de la operación (bicurrency)
 */

let cachedCountries = null;

/** Hydrate the module-level countries cache (called once from ParametersContext). */
export function setCountriesConfig(countries) {
  cachedCountries = countries || null;
}

/**
 * Resolve the configuration for a single country (fail-closed).
 * @param {string} country ISO country code ("CL" / "PE").
 * @param {Object<string, CountryConfig>} [countries] defaults to the cached map.
 * @returns {CountryConfig}
 */
export function getCountryConfig(country, countries = cachedCountries) {
  if (!country) {
    throw new Error(`[countryConfig] a country code is required but got "${country}"`);
  }
  if (!countries) {
    throw new Error('[countryConfig] countries config is not loaded');
  }
  const config = countries[country];
  if (!config) {
    throw new Error(`[countryConfig] no configuration found for country "${country}"`);
  }
  return config;
}

/**
 * Default "gastos de operación" for a fresh operation, per country AND per obligation
 * currency (bicurrency F1): `defaultGastoOpe` when the operation is in the country's
 * functional currency, `defaultGastoOpePorMoneda[moneda]` otherwise. No value → 0 (the
 * user types it); NEVER an FX conversion of the functional default — a management fee
 * does not move with the dollar. Fail-closed on a missing country config.
 * @param {string} country ISO country code of the operation's organización.
 * @param {string} [moneda] moneda de la operación; empty/undefined = functional.
 * @param {Object<string, CountryConfig>} [countries] defaults to the cached map.
 * @returns {number}
 */
export function getDefaultGastoOpe(country, moneda, countries = cachedCountries) {
  const config = getCountryConfig(country, countries);
  if (!moneda || moneda === config.currency) return config.defaultGastoOpe ?? 0;
  return config.defaultGastoOpePorMoneda?.[moneda] ?? 0;
}

/**
 * Límites del "monto máximo por transferencia" al dividir un giro en línea ("Dividir y Girar"),
 * en la moneda FUNCIONAL del país: `montoMaxTransfeMin` (piso del máximo por tramo) y
 * `montoMaxTransfeDefault` (valor inicial del diálogo). Data: `params/global.countries[cc]
 * .divisionGiro`; sin ella rigen los valores históricos del diálogo (CL: CLP 5.000.000 /
 * 7.000.000, las constantes anteriores a bicurrency — A7, revisión r9). Un giro en otra moneda
 * los convierte con el TC del giro (`limitesDivisionGiroEnMoneda`, giros/GirosList/utils/remesaME.js).
 * Fail-open: sin país o sin configuración → 0 (sin piso y sin valor inicial; el usuario digita).
 *
 * @param {string} [country] ISO country code de la org de la remesa.
 * @param {Object<string, CountryConfig>} [countries] defaults to the cached map.
 * @returns {{ montoMaxTransfeMin: number, montoMaxTransfeDefault: number }}
 */
const LIMITES_DIVISION_GIRO_HISTORICOS = { CL: { montoMaxTransfeMin: 5000000, montoMaxTransfeDefault: 7000000 } };

export function getLimitesDivisionGiro(country, countries = cachedCountries) {
  const config =
    (country && countries?.[country]?.divisionGiro) || (country && LIMITES_DIVISION_GIRO_HISTORICOS[country]) || {};
  return {
    montoMaxTransfeMin: config.montoMaxTransfeMin ?? 0,
    montoMaxTransfeDefault: config.montoMaxTransfeDefault ?? 0,
  };
}

/** True when the given country has a configuration block. */
export function hasCountryConfig(country, countries = cachedCountries) {
  return Boolean(country && countries && countries[country]);
}

/**
 * Currency-formatting config for a country code OR a `moneda` code.
 * Tries the country key first, then matches by currency code (e.g. "PEN").
 * @returns {{currency: string, currencySymbol: string, locale: string, decimals: number}}
 */
export function getCurrencyConfig(countryOrMoneda, countries = cachedCountries) {
  if (!countryOrMoneda) {
    throw new Error(`[countryConfig] a country/moneda code is required but got "${countryOrMoneda}"`);
  }
  if (!countries) {
    throw new Error('[countryConfig] countries config is not loaded');
  }
  const byCountry = countries[countryOrMoneda];
  const config =
    byCountry || Object.values(countries).find((c) => c.currency === countryOrMoneda);
  if (!config) {
    throw new Error(`[countryConfig] no currency configuration found for "${countryOrMoneda}"`);
  }
  return {
    currency: config.currency,
    currencySymbol: config.currencySymbol,
    locale: config.locale,
    decimals: config.decimals,
  };
}

/** Locale string for a country (fail-closed). */
export function getLocale(country, countries = cachedCountries) {
  return getCountryConfig(country, countries).locale;
}

/**
 * VAT/IGV rate carried on an organization snapshot (fail-closed).
 * @throws if the org carries no numeric VAT (no silent 0.19 fallback).
 */
export function getVatRate(org) {
  const candidate = Array.isArray(org) ? org[0] : org;
  const vat = candidate?.VAT;
  if (typeof vat !== 'number' || Number.isNaN(vat)) {
    throw new Error('[countryConfig] organizacion is missing a numeric VAT rate');
  }
  return vat;
}

/**
 * Best-effort country code from an org snapshot (single object or array).
 *
 * Pass `parametros` (the Parameters context / `parametrosDB`) to add the WS5
 * enrichment: a bare snapshot (auth-token shape `{group,text,value}` or an old
 * doc without a persisted `country`) is re-resolved by `value` against the full
 * `parametros.organizacion` list. Strict lookup — no match means `undefined`,
 * never an arbitrary `organizacion[0]` country.
 */
export function resolveOrgCountry(org, parametros) {
  const candidate = Array.isArray(org) ? org[0] : org;
  const country = candidate?.country;
  if (typeof country === 'string' && country) return country;
  if (country && typeof country === 'object' && typeof country.value === 'string') {
    return country.value;
  }
  const enriched = candidate?.value && parametros?.organizacion?.find((o) => o.value === candidate.value);
  if (enriched && enriched !== candidate) return resolveOrgCountry(enriched);
  return undefined;
}

/**
 * Functional currency of an organization (P17, r9 pendientes): the currency its ledger is kept in,
 * the same for every client of the org whatever `client.moneda` says. Never throws: '' when the
 * org's country or currency can't be resolved, so the caller decides (the back is fail-closed).
 *
 * @example monedaFuncionalDeOrg({ value: 'o4o' }, parametrosDB) // 'PEN'
 */
export function monedaFuncionalDeOrg(org, parametros) {
  try {
    const pais = resolveOrgCountry(org, parametros);
    return pais ? getCurrencyConfig(pais, parametros?.countries).currency : '';
  } catch (e) {
    return '';
  }
}

/**
 * Country-scoped view of the `params/global.bancos` catalog.
 *
 * `id` is the globally-unique key of a catalog entry; el `value` canónico es
 * "país + 3 dígitos" ("CL037", "PE002") — único global por contenido. El
 * filtro por país sigue siendo obligatorio en dropdowns de captura: no se
 * ofrecen bancos del otro país, y los CÓDIGOS crudos (CMF vs SBS) sí
 * colisionan entre países en los datos históricos sin prefijo.
 *
 * Fail-open on purpose (unlike the money selectors above): a missing country
 * argument or an untagged legacy entry keeps the full/entry list — a dropdown
 * with foreign banks beats an empty one that blocks data entry.
 *
 * @param {Array} bancos `parametrosDB.bancos`.
 * @param {string} [country] ISO country code ("CL" / "PE").
 * @returns {Array} the catalog restricted to `country`.
 */
export function getBancosForCountry(bancos, country) {
  const list = Array.isArray(bancos) ? bancos : [];
  if (!country) return list;
  return list.filter((banco) => !banco.country || banco.country === country);
}

/** Country code of a sucursal `value` from the params list (undefined without match). */
export function getSucursalCountry(parametros, sucursalValue) {
  return parametros?.sucursal?.find((suc) => suc.value === sucursalValue)?.country;
}

/**
 * Country of a form entity (client, deudor, garantía, prospecto...): the
 * entity's own `country` field when it maps to a configured country (a raw
 * world ISO like "AR" is ignored), else the country of its organization
 * resolved from the full params list via resolveActiveOrg (which falls back to
 * the first configured org — an org default always exists per user). This is
 * the WS2 idiom, centralized.
 *
 * @param {{ countries?: Object, organizacion?: Array }} parametros the Parameters context.
 * @param {{ country?: {value:string}|string, organizacion?: Object|Array }} values formik values / entity doc.
 * @returns {string|undefined}
 */
export function resolveEntityCountry(parametros, values) {
  const own = typeof values?.country === 'string' ? values.country : values?.country?.value;
  if (hasCountryConfig(own, parametros?.countries)) return own;
  const org = values?.organizacion;
  const orgValue = (Array.isArray(org) ? org[0] : org)?.value;
  return resolveOrgCountry(resolveActiveOrg(parametros, orgValue));
}

/**
 * Organizations of one country, for adding organizations to a client: an org of another country
 * keeps its books in another currency and ids. Fail-open when the country is unknown.
 *
 * @example orgsForCountry(parametrosDB.organizacionUser, 'CL', parametrosDB)
 */
export function orgsForCountry(orgs, country, parametros) {
  if (!country) return orgs || [];
  return (orgs || []).filter((org) => resolveOrgCountry(org, parametros) === country);
}

/** Bank catalog restricted to an org snapshot's country (fail-open on both ends). */
export function getBancosForOrg(parametros, org) {
  return getBancosForCountry(parametros?.bancos, resolveOrgCountry(org, parametros));
}

/** Bank name for a stored `value`, scoped by the row's sucursal country ('' when unresolvable). */
export function getBancoTextForSucursal(parametros, bancoValue, sucursalValue) {
  return getBanco(parametros?.bancos, bancoValue, getSucursalCountry(parametros, sucursalValue))?.text || '';
}

/**
 * Canonical form of a banco `value` for every lookup, comparison and
 * <Select>/<MenuItem> binding.
 *
 * Modelo de datos (flip 2026-08-10 en params/global.bancos): los values del
 * catálogo son STRINGS "código de país + 3 dígitos zero-padded" — "CL037",
 * "CL741", "PE002" — así la identidad (value, country) viaja dentro del dato
 * y el value es único GLOBAL por contenido (los códigos crudos colisionan
 * entre países: 009/049/053/055 existen en CL y PE). Las filas históricas
 * (client.bancos, bancosDestino, refModoPagoDoc, remesas, bancosSucursal)
 * persisten numbers o strings sin prefijo: TODO lo numérico es chileno por
 * regla de negocio (solo CL tenía datos antes del flip), así que aquí se
 * prefija con "CL". La entrada sentinel "Sin Giro" usa el value "NA" (país
 * agnóstico); su valor histórico 98 (numérico, reservado globalmente — los
 * docs viejos guardan banco: 98) normaliza a "NA".
 *
 * @param {*} value un banco `value` persistido o del catálogo, en cualquier era.
 * @returns {string} la forma canónica (`"CC###"`, o `"NA"` para el sentinel).
 */
export function normalizeBancoValue(value) {
  if (value === 'NA' || value === 98 || value === '98') return 'NA';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `CL${String(value).padStart(3, '0')}`;
  }
  if (typeof value === 'string' && /^\d+$/.test(value)) {
    return `CL${value.padStart(3, '0')}`;
  }
  return value;
}

/**
 * Filas de giro que generan transferencia: ni "Sin Giro" ("NA" / 98 histórico) ni
 * "Banco Simulado" ("CL097"). Esas filas no tienen moneda propia (la "Sin Giro" se
 * graba con moneda 'N/A'), así que no fijan la moneda de la remesa ni de su cuenta origen.
 * Espejo de `filaGiraDinero` en `apps/back/functions/src/utils/giroME.ts`.
 *
 * @param {{ banco?: * }} fila una fila de bancosDestino / ítem de remesa.
 * @returns {boolean}
 */
export function filaGiraDinero(fila) {
  const banco = normalizeBancoValue(fila?.banco);
  return banco !== 'NA' && banco !== 'CL097';
}

/**
 * Formas equivalentes bajo las que un value canónico puede estar persistido,
 * para queries de igualdad estricta que NO pueden normalizar el campo
 * almacenado (Firestore `==`/`in`): `where('banco', 'in', storedForms(v))`.
 *
 * Solo dos eras reales: canónica ("CL037") y numérica histórica (37, solo CL).
 * Espejo de `apps/back/functions/src/utils/bancos.ts` — mantener alineados.
 *
 * @param {*} value un banco `value` en cualquier era.
 * @returns {Array<string|number>} las formas a consultar (1 o 2 elementos).
 */
export function storedFormsOfBancoValue(value) {
  const canonical = normalizeBancoValue(value);
  if (canonical === 'NA') return ['NA', 98];
  const match = /^(CL|PE)(\d{3})$/.exec(String(canonical ?? ''));
  if (!match) return [canonical];
  if (match[1] === 'CL') return [canonical, Number(match[2])]; // "CL037" + 37 histórico
  return [canonical]; // PE no tiene datos históricos numéricos
}

/**
 * Catalog entry for a stored bank `value`, scoped to `country`.
 *
 * Ambos lados se comparan en forma canónica (normalizeBancoValue), así los
 * rows legacy (numéricos o sin prefijo — todos CL por regla de negocio)
 * resuelven contra el catálogo prefijado ("CL037"/"PE002") y viceversa.
 * Same fail-open contract as getBancosForCountry: without a country the
 * whole catalog is searched.
 *
 * @param {Array} bancos `parametrosDB.bancos`.
 * @param {*} value the persisted banco `value` (rows store value, not id).
 * @param {string} [country] ISO country code ("CL" / "PE").
 * @returns {Object|undefined} the catalog entry (`text`, `apiCode`, ...).
 */
export function getBanco(bancos, value, country) {
  const canonical = normalizeBancoValue(value);
  return getBancosForCountry(bancos, country).find((banco) => normalizeBancoValue(banco.value) === canonical);
}

/**
 * Resuelve el código de banco de una planilla de carga masiva contra el
 * catálogo del país de la operación y devuelve el `value` canónico.
 * Acepta el value canónico ("CL037"), el código crudo con o sin ceros
 * ("37", "037" — interpretado en `country`) o el sentinel "NA".
 *
 * @param {{ bancos?: Array }} parametros the Parameters context (`parametrosDB`).
 * @param {string} [country] país de la operación ("CL" / "PE").
 * @param {*} rawCode la celda de la planilla.
 * @returns {string|undefined} el `value` canónico del catálogo, o undefined si
 *   no resuelve (código inexistente o de otro país) — la fila debe corregirse,
 *   nunca coercionarse a número.
 */
export function resolveBancoFromCode(parametros, country, rawCode) {
  const input = String(rawCode ?? '').trim().toUpperCase();
  if (!input) return undefined;
  const match = /^(CL|PE)?(\d{1,3})$/.exec(input);
  const candidate = match ? `${match[1] || country || ''}${match[2].padStart(3, '0')}` : input;
  return getBancosForCountry(parametros?.bancos, country).find(
    (banco) => normalizeBancoValue(banco.value) === normalizeBancoValue(candidate)
  )?.value;
}

/**
 * Resolve an organization snapshot from the FULL params org list
 * (`parametros.organizacion`, which always carries `country` + a numeric `VAT`),
 * keyed by org `value`, falling back to the first configured org.
 *
 * Use this — NOT `parametros.organizacionUser[0]` — whenever you need an org's
 * country / currency / VAT. `organizacionUser` can contain the auth-token org
 * snapshot that `ParametersContext` pushes in (shaped `{ group, text, value }`
 * with no `country`/`VAT`), so resolving country/VAT off it makes the fail-closed
 * selectors throw and crash the render.
 *
 * @param {{ organizacion?: Array }} parametros the Parameters context (`parametrosDB`).
 * @param {string} [orgValue] preferred org `value` (user's active org, the form's org, ...).
 * @returns {Object|undefined} an org snapshot carrying `country`/`VAT`, or undefined if none loaded.
 */
export function resolveActiveOrg(parametros, orgValue) {
  const orgs = parametros?.organizacion;
  if (!Array.isArray(orgs) || orgs.length === 0) return undefined;
  return (orgValue && orgs.find((o) => o.value === orgValue)) || orgs[0];
}
